import { dataLayerPayload, type DataLayerPayload } from '../shared/data-layer.js';
import type { DrawingLayer } from '../shared/drawing-layer.js';
import { dataFeatureStyle, matchesFieldFilter } from '../shared/data-layer-fields.js';
import { runImportWorker } from './import-worker-client.js';
import { datasetBatches } from './local-dataset-store.js';
import type { DataFeature, DataLayer, DataLayerProvenance, ImportSourceFile } from '../shared/data-layer.js';
import type { SchemaRecord } from '../shared/schema-input.js';
import type { MapFileState } from '../shared/map-file-state.js';

interface ExportFeature {
  kmlSource?: DataFeature['kmlSource'];
  coordinateFields?: DataFeature['coordinateFields'];
  drawonamapLayerId?: string;
  drawonamapVisibility?: boolean;
  drawonamapSourceStyle?: Partial<DataLayer['style']>;
  visible?: boolean;
  image?: DataFeature['image'];
  type: 'Feature';
  id?: string | number;
  geometry: DataFeature['geometry'];
  properties: SchemaRecord | null;
  bbox?: number[];
  sourceStyle?: DataLayer['style'];
  drawonamapProvenance?: DataLayerProvenance;
}
interface ExportCollection {
  drawonamapMap?: MapFileState;
  drawonamapLayers?: DataLayerPayload['layer'][];
  drawonamapDrawingLayers?: DrawingLayer[];
  type: 'FeatureCollection';
  features: ExportFeature[];
  bbox?: number[];
  drawonamapSourceFiles?: ImportSourceFile[];
  drawonamapProvenance?: DataLayerProvenance;
}

export function dataLayersGeoJson(layers: DataLayer[]): ExportCollection {
  if (layers.some(layer => layer.dataset)) throw new Error('Stored datasets require the asynchronous GeoJSON export.');
  const output: ExportCollection = { type: 'FeatureCollection', features: [] };
  if (layers.length) output.drawonamapLayers = layers.map(layer => dataLayerPayload(layer).layer);
  if (layers.length === 1 && layers[0].bbox) output.bbox = layers[0].bbox;
  if (layers.length === 1 && layers[0].provenance) output.drawonamapProvenance = layers[0].provenance;
  const sources = layers.flatMap((layer) => layer.sourceFiles ?? []);
  if (sources.length && sources.length <= 32) output.drawonamapSourceFiles = sources;
  for (const layer of layers) {
    for (const feature of layer.features) {
      output.features.push(exportedDataFeature(layer, feature, layers.length === 1));
    }
  }
  return output;
}

function hasExportVisibility(layer: DataLayer, feature: DataFeature): boolean {
  return feature.visible !== undefined || !layer.visible || Boolean(layer.display?.fieldFilter) || Boolean(layer.display?.filters?.length);
}

function exportedDataFeature(layer: DataLayer, feature: DataFeature, metadataAtRoot = false): ExportFeature {
      const exported: ExportFeature = {
        type: 'Feature', drawonamapLayerId: layer.id, drawonamapVisibility: feature.visible !== false, drawonamapSourceStyle: feature.sourceStyle ?? {}, geometry: feature.geometry,
        properties: feature.nullProperties && !Object.keys(feature.properties).length ? null : feature.properties,
        sourceStyle: dataFeatureStyle(feature, layer),
      };
      if (hasExportVisibility(layer, feature)) exported.visible = layer.visible && feature.visible !== false && matchesFieldFilter(feature, layer.display);
      if (feature.kmlSource) exported.kmlSource = feature.kmlSource;
      if (feature.image) exported.image = feature.image;
      if (feature.coordinateFields) exported.coordinateFields = feature.coordinateFields;
      const provenance = feature.provenance ?? layer.provenance;
      if (provenance && !(metadataAtRoot && !feature.provenance && provenance.originalMetadata)) exported.drawonamapProvenance = provenance;
      if (feature.sourceKey !== undefined) exported.id = feature.sourceKey;
      if (feature.bbox) exported.bbox = feature.bbox;
  return exported;
}

/** Produce bounded Blob parts and yield between batches. Original binary files
 * remain separate downloads, so exports do not recursively embed large inputs. */
export async function dataLayersGeoJsonBlob(layers: DataLayer[], initial: ExportFeature[] = [], drawingLayers?: DrawingLayer[], map?: MapFileState): Promise<Blob> {
  return layers.some(layer => layer.dataset) ? runImportWorker('exportGeoJson', { layers, initial, drawingLayers, map }) : encodeGeoJsonBlob({ layers, initial, drawingLayers, map });
}

export async function encodeGeoJsonBlob({ layers, initial = [], drawingLayers, map }: { layers: DataLayer[]; initial?: ExportFeature[]; drawingLayers?: DrawingLayer[]; map?: MapFileState }): Promise<Blob> {
  const { features: _features, ...metadata } = dataLayersGeoJson(layers.map(layer => ({ ...layer, dataset: undefined, features: [] })));
  if (drawingLayers) metadata.drawonamapDrawingLayers = drawingLayers;
  if (map) metadata.drawonamapMap = map;
  if (initial.length) { delete metadata.bbox; delete metadata.drawonamapProvenance; }
  const parts: Blob[] = [];
  let text = JSON.stringify(metadata).slice(0, -1) + ',"features":[';
  let first = true;
  let count = 0;
  async function append(feature: ExportFeature) {
    text += (first ? '' : ',') + JSON.stringify(feature);
    first = false;
    if (text.length >= 512 * 1024) { parts.push(new Blob([text])); text = ''; }
    if (++count % 512 === 0) await new Promise(resolve => setTimeout(resolve, 0));
  }
  for (const feature of initial) await append(feature);
  for (const layer of layers) {
    if (layer.dataset) {
      for await (const batch of datasetBatches(layer.dataset)) for (const feature of batch) await append(exportedDataFeature(layer, feature, layers.length === 1));
    } else for (const feature of layer.features) await append(exportedDataFeature(layer, feature, layers.length === 1));
  }
  parts.push(new Blob([text + ']}']));
  return new Blob(parts, { type: 'application/geo+json' });
}
